import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import GlowRing from '../../../components/ui/GlowRing';

const StepCounter = ({ currentSteps, dailyGoal, onGoalUpdate }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [displaySteps, setDisplaySteps] = useState(0);

  const progressPercentage = Math.min((currentSteps / dailyGoal) * 100, 100);
  const circumference = 2 * Math.PI * 45; // radius = 45
  const strokeDashoffset = circumference - (progressPercentage / 100) * circumference;

  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => {
      setIsAnimating(false);
    }, 1000);

    // Animate step counter
    const stepTimer = setInterval(() => {
      setDisplaySteps(prev => {
        if (prev < currentSteps) {
          return Math.min(prev + Math.ceil((currentSteps - prev) / 10), currentSteps);
        }
        return prev;
      });
    }, 50);

    return () => {
      clearTimeout(timer);
      clearInterval(stepTimer);
    };
  }, [currentSteps]);

  const getMotivationalMessage = () => {
    const percentage = (currentSteps / dailyGoal) * 100;
    if (percentage >= 100) return "🎉 Goal achieved! Amazing work!";
    if (percentage >= 75) return "🔥 Almost there! Keep going!";
    if (percentage >= 50) return "💪 Halfway there! You're doing great!";
    if (percentage >= 25) return "🚀 Good start! Keep moving!";
    return "👟 Let's get those steps in!";
  };

  const handleGoalAdjustment = (adjustment) => {
    const newGoal = Math.max(1000, dailyGoal + adjustment);
    onGoalUpdate(newGoal);
  };

  return (
    <div className="glass-card p-6 shadow-neon-lime">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg" style={{ background: 'rgba(170,255,0,0.1)' }}>
            <Icon name="Footprints" size={20} style={{ color: '#AAFF00' }} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground" style={{ fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>Daily Steps</h3>
            <p className="text-sm text-muted-foreground">Real-time step tracking</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={() => handleGoalAdjustment(-1000)} iconName="Minus" disabled={dailyGoal <= 1000} />
          <span className="text-sm font-medium text-foreground px-2">Goal: {dailyGoal?.toLocaleString()}</span>
          <Button variant="outline" size="sm" onClick={() => handleGoalAdjustment(1000)} iconName="Plus" />
        </div>
      </div>
      <div className="flex items-center justify-center mb-6">
        <GlowRing
          value={displaySteps}
          max={dailyGoal}
          color="lime"
          size={160}
          unit="steps"
          label="Steps"
          displayValue={displaySteps?.toLocaleString()}
          sublabel={`${progressPercentage?.toFixed(0)}%`}
        />
      </div>
      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center">
          <div className="text-lg font-semibold font-metric" style={{ color: '#AAFF00' }}>
            {Math.max(0, dailyGoal - currentSteps)?.toLocaleString()}
          </div>
          <div className="text-xs text-muted-foreground">Remaining</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold font-metric" style={{ color: '#00D4FF' }}>
            {Math.round(currentSteps * 0.0005 * 10) / 10} km
          </div>
          <div className="text-xs text-muted-foreground">Distance</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold font-metric" style={{ color: '#FF6B00' }}>
            {Math.round(currentSteps * 0.04)}
          </div>
          <div className="text-xs text-muted-foreground">Calories</div>
        </div>
      </div>
      {/* Motivational Message */}
      <div className="rounded-lg p-4 text-center" style={{ background: 'rgba(170,255,0,0.05)', border: '1px solid rgba(170,255,0,0.15)' }}>
        <p className="text-sm font-medium text-foreground">{getMotivationalMessage()}</p>
        {progressPercentage < 100 && (
          <p className="text-xs text-muted-foreground mt-1">
            {Math.max(0, dailyGoal - currentSteps)?.toLocaleString()} more steps to reach your goal
          </p>
        )}
      </div>
      {/* Quick Actions */}
      <div className="flex items-center justify-center space-x-2 mt-4">
        <Button variant="outline" size="sm" iconName="RotateCcw" iconPosition="left" onClick={() => console.log('Reset daily steps')}>Reset</Button>
        <Button variant="outline" size="sm" iconName="Share2" iconPosition="left" onClick={() => console.log('Share progress')}>Share</Button>
      </div>
    </div>
  );
};

export default StepCounter;